<script setup>
  import Header from '@/components/Header.vue'
  import Footer from '@/components/Footer.vue'
  import { ref, onMounted } from 'vue'
  import { useRouter } from 'vue-router'
  import { useDialogStore } from '@/stores/dialogStore'
  import { useContractStore } from '@/stores/contractStore'
  import { useUserStore } from '@/stores/userStore'

  const router = useRouter()
  const dialogStore = useDialogStore()
  const contractStore = useContractStore()
  const userStore = useUserStore()

  const breadcrumbs = [{ name: 'ダッシュボード', url: 'dashboard' }, { name: '契約確認', url: 'contract' }, { name: 'プラン契約' }]

  // methods ///////////////////////////////////////////////////////////////

  onMounted(async () => {
    // ユーザー情報を取得
    await userStore.getCurrentUser()

    // 権限チェック（rootユーザーのみ許可）
    if (!userStore.isRoot()) {
      await dialogStore.showAlertDialog('権限エラー', 'この機能にアクセスする権限がありません。')
      router.push('/dashboard')
      return
    }

    // プランの価格情報を取得
    await contractStore.getPlanPrices()

    // 現在の契約情報を取得
    await contractStore.getCurrentContract()
  })

  // プラン選択処理
  const onSelectPlan = async planType => {
    const planName = planType === 'monthly' ? '月間プラン' : '年間プラン'

    // 現在の契約と同じプランの場合は何もしない
    if (contractStore.isCurrentPlan(planType)) {
      return
    }

    // 現在有料契約中かどうかで処理を分岐
    const isPaidUser = contractStore.isPaidStatus
    const actionText = isPaidUser ? '変更' : '契約'

    // 確認ダイアログを表示
    if (await dialogStore.showConfirmDialog('確認', `${planName}に${actionText}しますか？`)) {
      let message
      if (isPaidUser) {
        // 有料契約中の場合はプラン変更
        message = await contractStore.changePlan(planType)
      } else {
        // 未契約の場合は新規契約開始
        message = await contractStore.startPaidContract(planType)
      }

      if (message) {
        await dialogStore.showMessageDialog('成功', message)
        // 契約確認画面に戻る
        router.push('/contract')
      }
    }
  }

  // プランボタンが無効かどうかを判定
  const isPlanDisabled = planType => {
    return contractStore.isCurrentPlan(planType)
  }

  // プランボタンのテキストを取得
  const getPlanButtonText = planType => {
    if (isPlanDisabled(planType)) {
      return '契約中'
    }

    // 有料契約中かどうかでボタンテキストを変更
    const isPaidUser = contractStore.isPaidStatus
    const actionText = isPaidUser ? '変更' : '契約'

    return planType === 'monthly' ? `月間プランに${actionText}` : `年間プランに${actionText}`
  }

  // 戻るボタン
  const onBack = () => {
    router.push('/contract')
  }
</script>

<template>
  <Header />
  <main class="app-main">
    <div class="main-header">
      <button type="button" class="back" @click="onBack">
        <span class="material-symbols-outlined">arrow_back</span>
      </button>
      <h2>プラン契約</h2>
    </div>
    <div class="main-body">
      <div class="subscription-content">
        <!-- プランの説明 -->
        <section class="plan-description">
          <h3>料金プラン</h3>
          <!-- <div class="plan-image-placeholder">
            <p>【プラン詳細画像】</p>
            <p>月間プラン: 毎月課金、いつでも変更・解約可能</p>
            <p>年間プラン: 年間一括払い、月間プランより割安</p>
          </div> -->
          <div class="plan-buttons">
            <div class="plan-card">
              <h4>年間プラン</h4>
              <div class="plan-details">
                <p class="price">
                  {{ contractStore.yearlyPriceFormatted }}
                  <span class="period">/年(税込)</span>
                </p>
              </div>
              <button class="btn btn-primary" :class="{ 'btn-disabled': isPlanDisabled('yearly') }" :disabled="isPlanDisabled('yearly')" @click="onSelectPlan('yearly')">
                {{ getPlanButtonText('yearly') }}
              </button>
            </div>

            <div class="plan-card">
              <h4>月間プラン</h4>
              <div class="plan-details">
                <p class="price">
                  {{ contractStore.monthlyPriceFormatted }}
                  <span class="period">/月(税込)</span>
                </p>
              </div>
              <button class="btn btn-primary" :class="{ 'btn-disabled': isPlanDisabled('monthly') }" :disabled="isPlanDisabled('monthly')" @click="onSelectPlan('monthly')">
                {{ getPlanButtonText('monthly') }}
              </button>
            </div>
          </div>
        </section>

        <!-- 支払いルールの説明 -->
        <section class="payment-rules">
          <h3>支払いルール</h3>
          <ul class="rules-list">
            <li>ご契約の料金は、契約月および更新月の末日にまとめてご請求いたします。</li>
            <li>ご契約後は特別なお手続きなく自動的に更新され、継続して安心してご利用いただけます。</li>
            <li>&nbsp;</li>
            <li>ご利用を終了される場合も、契約期間の満了日まではシステムをご活用いただけます。</li>
            <li>契約満了後は自動的に無料プランへ移行いたします。</li>
            <li>無料プランでは一部機能が制限されますが、これまでにご利用いただいたデータを引き続きご覧いただけます（※ストレージ機能は対象外となります）。</li>
          </ul>
        </section>

        <!-- プランの選択 -->
        <!-- <section class="plan-selection">
          <h3>プラン選択</h3>
        </section> -->
      </div>
    </div>
  </main>
  <Footer :breadcrumbs="breadcrumbs" />
</template>

<style lang="less" scoped>
  .subscription-content {
    margin: 0 auto;
    padding: 20px 60px;
    width: 100%;
  }

  .main-header {
    display: flex;
    align-items: center;
    margin-bottom: 20px;

    h2 {
      flex-grow: 1;
      margin: 0;
      margin-left: 10px;
    }
  }

  .material-symbols-outlined {
    vertical-align: middle;
  }

  section {
    margin-bottom: 40px;

    h3 {
      font-size: 18px;
      font-weight: bold;
      margin-bottom: 16px;
      color: #333;
      border-bottom: 2px solid #1a7247;
      padding-bottom: 8px;
    }
  }

  .plan-image-placeholder {
    background-color: #f8f9fa;
    border: 2px dashed #dee2e6;
    border-radius: 8px;
    padding: 40px;
    text-align: center;
    color: #6c757d;
    max-width: 800px;
    margin: 0 auto;

    @media (min-width: 1200px) {
      max-width: 900px;
    }

    @media (min-width: 1600px) {
      max-width: 1000px;
    }

    @media (max-width: 768px) {
      max-width: 400px;
    }

    p {
      margin: 8px 0;
      font-size: 14px;
    }
  }

  .rules-list {
    background-color: #f8f9fa;
    border-radius: 8px;
    padding: 20px;
    margin: 0 auto;
    max-width: 800px;

    @media (min-width: 1200px) {
      max-width: 900px;
    }

    @media (min-width: 1600px) {
      max-width: 1000px;
    }

    @media (max-width: 768px) {
      max-width: 400px;
    }

    li {
      margin-bottom: 12px;
      line-height: 1.6;
      font-size: 14px;
      color: #495057;

      &:last-child {
        margin-bottom: 0;
      }
    }
  }

  .plan-buttons {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 30px;
    max-width: 800px;
    margin: 0 auto;

    @media (min-width: 1200px) {
      gap: 40px;
      max-width: 900px;
    }

    @media (min-width: 1600px) {
      gap: 50px;
      max-width: 1000px;
    }

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
      max-width: 400px;
    }
  }

  .plan-card {
    background-color: #fff;
    border: 2px solid #e9ecef;
    border-radius: 12px;
    padding: 30px;
    text-align: center;
    transition: all 0.3s ease;

    &:hover {
      border-color: #1a7247;
      box-shadow: 0 4px 12px rgba(26, 114, 71, 0.15);
    }

    h4 {
      font-size: 20px;
      font-weight: bold;
      margin-bottom: 20px;
      color: #333;
    }
  }

  .plan-details {
    margin-bottom: 30px;

    .price {
      font-size: 28px;
      font-weight: bold;
      color: #1a7247;
      margin-bottom: 8px;

      .period {
        font-size: 16px;
        color: #6c757d;
        font-weight: normal;
      }
    }

    .description {
      font-size: 14px;
      color: #6c757d;
      margin: 0;
    }
  }

  .btn {
    background-color: #1a7247;
    color: #fff;
    border: none;
    border-radius: 8px;
    padding: 12px 24px;
    font-size: 16px;
    font-weight: bold;
    cursor: pointer;
    transition: all 0.3s ease;
    width: 100%;

    &:hover:not(:disabled) {
      background-color: #145a38;
      transform: translateY(-2px);
    }

    &.btn-primary {
      background-color: #1a7247;

      &:hover:not(:disabled) {
        background-color: #145a38;
      }
    }

    &.btn-disabled,
    &:disabled {
      background-color: #6c757d;
      color: #fff;
      cursor: not-allowed;
      transform: none;

      &:hover {
        background-color: #6c757d;
        transform: none;
      }
    }
  }
</style>
